import { useEffect, useState } from 'react';
import { useT, type MessageKey } from '../../i18n/i18n';
import {
  billingStore,
  ensureBillingStarted,
  priceLabel,
  purchaseAddOn,
  purchaseAnnual,
  purchaseMonthly,
  purchaseSingle,
  restorePurchases,
  type PurchaseOptions,
  type PurchaseResult
} from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import type { BillingProductKey } from '../../../supabase/functions/_shared/billingPolicy';
import { Button, InlineMessage, SheetShell } from '../../ui/components';
import { useOnlineStatus } from '../../ui/hooks';
import { isSubscriptionKey, PERIOD_KEYS, purchaseMessage } from '../settings/PlanPage';
import { AutoRenewConsent } from './AutoRenewConsent';
import { PrivacyPage, TermsPage } from '../settings/SettingsPages';
import '../../ui/billing.css';
import { storeKey } from './storeCopy';

// "A plan or a single purchase is needed" — the sheet the New Inspection flow opens when a FORMAL
// inspection has nothing to pay for it (Owner decisions 2026-10-19 P-5: a single report or a plan). A
// Demo inspection never reaches this sheet (X-4).
//
// It sells the three things that unlock a formal inspection; the 3-pack is not here because it buys AI
// 单 for a subscriber, not the inspection itself. Every purchase happens in the store's own sheet — no
// card form, no discount arithmetic, prices fixed. App Store Review 3.1.2: each offer shows its price as
// the most prominent figure with its period, and the sheet carries the subscription terms and the links
// to the Terms of Use, the Privacy Policy and (iOS) Apple's standard EULA. The two in-app documents open
// in place of the sheet, so the New Inspection form underneath is never left.
//
// The caller (New Inspection) owns what happens next: onPurchased fires as soon as the store
// confirms, so the screen can continue creating the inspection.
const OPTIONS: ReadonlyArray<{ key: BillingProductKey; nameKey: MessageKey; bodyKey: MessageKey; buy: (options?: PurchaseOptions) => Promise<PurchaseResult> }> = Object.freeze([
  { key: 'single', nameKey: 'settings.planSingle', bodyKey: 'billing.singleBody', buy: purchaseSingle },
  { key: 'monthly', nameKey: 'settings.planMonthly', bodyKey: 'billing.monthlyBody', buy: purchaseMonthly },
  { key: 'annual', nameKey: 'settings.planAnnual', bodyKey: 'billing.annualBody', buy: purchaseAnnual }
]);

// mode 'addOn': the AI card's 「买「加 3 单包」」 when a subscriber's 单 are used up — the pack alone.
const ADD_ON_OPTIONS: typeof OPTIONS = Object.freeze([{ key: 'addOn', nameKey: 'settings.planAddon', bodyKey: 'billing.addOnBody', buy: purchaseAddOn }]);

// The product's own Terms of Service, on every platform (Owner decision list 2026-10-19 items 5 / 6).
import { TERMS_OF_SERVICE_URL } from '../settings/legalLinks';

export function PurchaseSheet(props: { onClose: () => void; onPurchased?: (product: BillingProductKey) => void; mode?: 'inspection' | 'addOn' }) {
  const { t } = useT();
  const online = useOnlineStatus();
  const state = useStore(billingStore, (s) => s);
  const [doc, setDoc] = useState<'terms' | 'privacy' | null>(null);

  useEffect(() => {
    void ensureBillingStarted();
  }, []);

  const purchase = purchaseMessage(state.lastPurchase);
  const canPurchase = state.storeConfigured && online && state.busy === null;

  const addOn = props.mode === 'addOn';
  const options = addOn ? ADD_ON_OPTIONS : OPTIONS;
  // Web version (item 39): the automatic-renewal tick the subscription buttons need.
  const web = state.storePlatform === 'web';
  const sellsSubscriptions = options.some((option) => isSubscriptionKey(option.key));
  const [renewConsent, setRenewConsent] = useState(false);
  const [renewMissing, setRenewMissing] = useState(false);

  async function run(option: (typeof OPTIONS)[number]) {
    if (web && isSubscriptionKey(option.key) && !renewConsent) {
      setRenewMissing(true);
      return;
    }
    const result = await option.buy({ autoRenewConsent: renewConsent });
    if (result.kind === 'purchased') props.onPurchased?.(option.key);
  }

  if (doc) {
    return (
      <SheetShell full title={t(doc === 'terms' ? 'settings.terms' : 'settings.privacy')} onClose={() => setDoc(null)} testId="purchase-doc-sheet">
        {doc === 'terms' ? <TermsPage /> : <PrivacyPage />}
      </SheetShell>
    );
  }

  return (
    <SheetShell title={t(addOn ? 'billing.addOnSheetTitle' : 'billing.sheetTitle')} onClose={props.onClose} testId="purchase-sheet">
      <p className="card-body">{!online ? t('billing.offline') : addOn ? t('billing.addOnSheetBody') : t('billing.sheetBody')}</p>
      {state.storePlatform === 'fake' ? <InlineMessage kind="info" text={t('billing.simulated')} /> : null}
      {!state.storeConfigured ? <InlineMessage kind="err" text={t('billing.notConfigured')} /> : null}
      {web && sellsSubscriptions ? (
        <AutoRenewConsent
          checked={renewConsent}
          missing={renewMissing}
          onChange={(value) => {
            setRenewConsent(value);
            if (value) setRenewMissing(false);
          }}
          testId="purchase-auto-renew"
        />
      ) : null}
      <div className="billing-sheet-options">
        {options.map((option) => (
          <div className="billing-plan" key={option.key} data-testid={`purchase-option-${option.key}`}>
            <div className="billing-plan-head">
              <span className="billing-plan-name">{t(option.nameKey)}</span>
              <span className="billing-plan-price" data-testid={`purchase-price-${option.key}`}>
                {priceLabel(option.key)}
                <span className="billing-plan-period">{t(PERIOD_KEYS[option.key])}</span>
              </span>
            </div>
            <p className="billing-plan-body">{t(option.bodyKey)}</p>
            <Button variant={option.key === 'single' ? 'primary' : 'secondary'} icon="credit_card" disabled={!canPurchase} onClick={() => void run(option)} testId={`purchase-buy-${option.key}`}>
              {state.busy === option.key ? t(storeKey('billing.buying')) : t(storeKey('billing.buy'))}
            </Button>
          </div>
        ))}
      </div>
      {purchase ? <InlineMessage kind={purchase.kind} text={t(storeKey(purchase.key))} /> : null}
      {/* On the web the renewal terms already sit above the tick; the line about periods not carrying over stays. */}
      <ul className="billing-terms" data-testid="purchase-terms">
        {web ? null : <li>{t(storeKey('billing.terms1'))}</li>}
        <li>{t('billing.terms3')}</li>
        {web ? null : <li>{t(storeKey('billing.terms5'))}</li>}
      </ul>
      <div className="card-actions">
        <Button variant="text" icon="restart_alt" disabled={!state.storeConfigured || !online || state.busy !== null} onClick={() => void restorePurchases()} testId="purchase-restore">
          {t('billing.restore')}
        </Button>
        <Button variant="text" icon="gavel" onClick={() => setDoc('terms')} testId="purchase-terms-link">
          {t('settings.terms')}
        </Button>
        <Button variant="text" icon="privacy_tip" onClick={() => setDoc('privacy')} testId="purchase-privacy-link">
          {t('settings.privacy')}
        </Button>
        <Button variant="text" icon="description" onClick={() => window.open(TERMS_OF_SERVICE_URL, '_blank')} testId="purchase-eula-link">
          {t('billing.eula')}
        </Button>
      </div>
    </SheetShell>
  );
}
